/*
 * ce-cart-addon.js — the cart drawer add-on switch (snippets/ce-cart-addon.liquid).
 *
 * WHY THIS SHAPE (2026-09-02, revised for speed then for spam-clicking):
 *   - The total moves AT ONCE: the drawer renders BOTH totals (with and without the add-on, formatted by Liquid so
 *     every market/currency is right). The switch just shows the one matching its state — an idempotent read, so
 *     flicking the switch twenty times can never drift the number.
 *   - Cart writes are SERIALISED and only the last intention counts: every flick updates `target`; one worker sends
 *     /cart/update.js until the cart matches `target` (the endpoint is idempotent). No request is ever "ignored"
 *     while another is in flight — that was what made a spammed switch end up disagreeing with the cart.
 *   - No section rendering is requested: the server does not rebuild the drawer for a switch. We only compare the
 *     returned cart total with what we display and re-sync in the rare case they disagree (a discount that depends
 *     on the cart, for instance).
 *   - Switching OFF also writes a private cart attribute (`_ce_addon_off`), so an add-on that is ON by default does
 *     not come back on the next add-to-cart. Switching ON clears it.
 *   - Delegated listener in the CAPTURE phase with stopPropagation: the drawer is re-rendered by innerHTML on every
 *     cart change, and Dawn's <cart-drawer-items> validates any `change` reaching it as a quantity input.
 */
(() => {
  const SELECTOR = '[data-ce-addon-variant]';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const totalNode = () => document.querySelector('#CartDrawer [data-ce-total]');

  const state = { target: null, running: false, cartOn: null };

  // the "add it with the next add-to-cart" marker lives in the page (layout/theme.liquid). Switching the add-on off
  // must remove it at once, otherwise the next add-to-cart puts the product back although the cart says "off".
  const syncMarker = (variantId, on) => {
    const existing = document.querySelector(`[data-ce-preadd-variant="${variantId}"]`);
    if (on && !existing) {
      const marker = document.createElement('span');
      marker.hidden = true;
      marker.dataset.cePreaddVariant = variantId;
      document.body.appendChild(marker);
    }
    if (!on && existing) existing.remove();
  };

  // show the total that matches `on` — always from the values the server rendered, never by accumulating
  const paintTotal = (on) => {
    const node = totalNode();
    if (!node) return null;
    const html = on ? node.dataset.onHtml : node.dataset.offHtml;
    const cents = on ? node.dataset.onCents : node.dataset.offCents;
    if (html === undefined) return null;
    node.innerHTML = html;
    return Number(cents);
  };

  const resync = async () => {
    try {
      const ids = ['cart-drawer', 'cart-icon-bubble'];
      const html = await (await fetch(`${window.location.pathname}?sections=${ids.join(',')}`)).json();
      const doc = new DOMParser().parseFromString(html['cart-drawer'] || '', 'text/html');
      ['.ce-cd__summary', '.ce-cd__fs'].forEach((selector) => {
        const fresh = doc.querySelector(selector);
        const current = document.querySelector(`#CartDrawer ${selector}`);
        if (fresh && current) current.replaceWith(fresh);
        else if (!fresh && current) current.remove();
      });
      const bubble = document.querySelector('#cart-icon-bubble');
      const freshBubble = new DOMParser()
        .parseFromString(html['cart-icon-bubble'] || '', 'text/html')
        .querySelector('#cart-icon-bubble');
      if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    } catch (error) {
      /* leave what is on screen: the next cart action re-renders everything anyway */
    }
  };

  const run = async (input) => {
    if (state.running) return; // one worker; it will pick up the latest target before it stops
    state.running = true;
    try {
      while (state.target !== state.cartOn) {
        const on = state.target;
        const variantId = input.dataset.ceAddonVariant;
        const body = {
          updates: { [variantId]: on ? 1 : 0 },
          attributes: { _ce_addon_off: on ? '' : '1' },
        };
        let cart = null;
        try {
          const response = await fetch(`${root()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          cart = await response.json();
          if (!response.ok || cart.status) throw new Error(cart.description || 'cart update failed');
        } catch (error) {
          // the cart did not move: put the switch and the total back where the cart really is
          state.target = state.cartOn;
          input.checked = state.cartOn;
          paintTotal(state.cartOn);
          break;
        }
        state.cartOn = (cart.items || []).some((item) => String(item.variant_id) === variantId);
        const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
        if (bubble) bubble.textContent = cart.item_count;
        if (state.target === state.cartOn) {
          const shown = paintTotal(state.cartOn);
          if (shown !== null && Number(cart.total_price) !== shown) await resync();
          document.dispatchEvent(new CustomEvent('ce:addon-changed', { detail: { variantId, on: state.cartOn } }));
        }
      }
    } finally {
      state.running = false;
    }
  };

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest(SELECTOR);
      if (!input) return;
      event.stopPropagation();
      if (state.cartOn === null) state.cartOn = input.dataset.ceAddonOn === 'true';
      state.target = input.checked;
      paintTotal(state.target); // instant, and correct however fast the switch is flicked
      syncMarker(input.dataset.ceAddonVariant, state.target);
      run(input);
    },
    true
  );
})();
